/** The Purchases hub's section list.
 *
 *  Lives outside PurchasesView for the same reason salesSections.ts sits
 *  outside SalesView: two things render it — the hub itself (as its content
 *  router) and AppSidebar, which SWAPS the app nav for these while Purchases
 *  is open — the same drill-in the Workspaces, Employers and Sales directories
 *  make. Purchases previously drew its own second column beside the app nav,
 *  which is the one layout the drill-in rule exists to avoid.
 *
 *  Unlike Sales, there is one audience here: every workspace buys and pays
 *  its own bills, so there is currently no section with a platform-only
 *  equivalent. `adminOnly` and `visiblePurchasesSections` are kept anyway,
 *  in the same shape Sales needed, so a future platform-only section (say,
 *  Contract Group's own supplier spend) slots in without a retrofit.
 */

import {
  LayoutDashboard,
  FileText,
  CreditCard,
  ClipboardList,
  Receipt,
  Store,
  Settings,
} from '@/shared/ui/icons';

export type PurchasesSectionId =
  | 'overview'
  | 'bills'
  | 'online-bill-payments'
  | 'purchase-orders'
  | 'expenses'
  | 'merchants'
  | 'settings';

export interface PurchasesSection {
  id: PurchasesSectionId;
  label: string;
  Icon: React.ElementType;
  /** No section is admin-only today — see the file header. */
  adminOnly?: boolean;
}

export const PURCHASES_SECTIONS: PurchasesSection[] = [
  { id: 'overview',             label: 'Purchases Overview',   Icon: LayoutDashboard },
  { id: 'bills',                label: 'Bills',                Icon: FileText },
  { id: 'online-bill-payments', label: 'Online Bill Payments', Icon: CreditCard },
  { id: 'purchase-orders',      label: 'Purchase Orders',      Icon: ClipboardList },
  { id: 'expenses',             label: 'Expenses',             Icon: Receipt },
  { id: 'merchants',            label: 'Merchants',            Icon: Store },
  { id: 'settings',             label: 'Purchase Settings',    Icon: Settings },
];

export function visiblePurchasesSections(_role?: string): PurchasesSection[] {
  // Straight pass-through today (see file header). Routed through a function
  // rather than exported raw so a caller reads the gate, not the list — the
  // habit that matters the day a section does need filtering.
  return PURCHASES_SECTIONS;
}

/** Sections that are still placeholders. Kept here so the hub and any nav that
 *  wants to mark them agree on which are real. */
export const PURCHASES_COMING_SOON: Partial<Record<PurchasesSectionId, string>> = {
  'online-bill-payments': 'Online Bill Payments will be available soon.',
  'settings':             'Purchase Settings will be available soon.',
};
